import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Actions from "../actions/Actions";
import styles from "./Reveal.module.scss";
import "react-pdf/dist/Page/TextLayer.css";
import Button from "@components/button/Button";
import { useStudentLessonContent } from "@src/hooks/useStudentLessonContent";
import RotateIcon from "@src/assets/icons/rotate.svg?react";
import RevealSlide from "@src/components/reveal-slide/RevealSlide";
import CompletingModal from "../../complete-modal/CompletingModal";

type Props = {
  data: string;
  isFullScreen: boolean;
  isPreview?: boolean;
  goBack?: () => void;
  pageNumber: number;
  changePageNumber: (value: number) => void;
  startLesson?: () => void;
};

function RevealSlideView(props: Props) {
  const {
    data,
    isFullScreen,
    isPreview,
    goBack,
    pageNumber,
    changePageNumber,
  } = props;
  const { totalPages, setTotalPages } = useStudentLessonContent();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const [height, setHeight] = useState<number | undefined>(undefined);
  const [completeModal, setCompleteModal] = useState(false);

  useEffect(() => {
    setTotalPages(1);
  }, [data, setTotalPages]);

  const calculateSize = useCallback(() => {
    if (wrapperRef.current) {
      const { width, height } = wrapperRef?.current?.getBoundingClientRect();
      var w =
        window.innerWidth ||
        document.documentElement.clientWidth ||
        document.body.clientWidth;
      setWidth(w > width ? width : w);
      setHeight(height);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("orientationchange", calculateSize);

    return () => {
      window.removeEventListener("orientationchange", calculateSize);
    };
  }, [calculateSize]);

  useLayoutEffect(() => {
    window.addEventListener("resize", calculateSize);

    calculateSize();
    return () => {
      window.removeEventListener("resize", calculateSize);
    };
  }, [calculateSize, isFullScreen]);

  useEffect(() => {
    if (width) {
      setTimeout(calculateSize, 50);
    }
  }, [calculateSize, width]);

  const prevDisabled = useMemo(() => pageNumber <= 1, [pageNumber]);
  const isLast = useMemo(
    () => pageNumber >= totalPages,
    [pageNumber, totalPages]
  );

  const handleLeft = useCallback(() => {
    changePageNumber(Math.max(pageNumber - 1, 1));
  }, [pageNumber, changePageNumber]);

  const handleRight = useCallback(() => {
    changePageNumber(Math.min(pageNumber + 1, totalPages));
  }, [totalPages, pageNumber, changePageNumber]);

  const prevButton = useMemo(() => {
    if (prevDisabled) return null;
    return (
      <Button
        size={"extraSmall"}
        onClick={handleLeft}
        className={isPreview ? styles.previousBtn : styles.previousBlackBtn}
        label={"PREVIOUS"}
      />
    );
  }, [prevDisabled, isPreview, handleLeft]);

  const nextButton = useMemo(() => {
    if (isLast) return null;
    return <Button size={"extraSmall"} onClick={handleRight} label={"NEXT"} />;
  }, [isLast, handleRight]);

  return (
    <Actions
      fullScreen={isFullScreen}
      leftButton={prevButton}
      rightButton={nextButton}
      totalPages={totalPages}
      pageNumber={pageNumber}
      isPreview={isPreview}
      goBack={goBack}
    >
      <div
        className={`${styles.wrapper} ${
          !isPreview ? styles.blackfullScreen : ""
        }`}
        ref={wrapperRef}
      >
        <div className={isFullScreen ? styles.fullContent : styles.content}>
          <div
            style={{
              width: `${width}px`,
              height: `${height}px`,
            }}
          >
            <RevealSlide
              pageNumber={pageNumber}
              fullScreen={isFullScreen}
              isPreview={isPreview}
              totalPages={totalPages}
              setTotalPages={setTotalPages}
              key={data}
              data={data}
              style={{
                width,
                height,
              }}
            />
          </div>
        </div>
        <div className={styles.rotate}>
          <RotateIcon width="60" height="60" className={styles.rorateIcon} />
          <div className={styles.rotateTitle}>Rotate your phone,</div>
          <div className={styles.rotateDetail}>
            for a better reading experience.
          </div>
        </div>
      </div>
      {completeModal ? (
        <CompletingModal
          open={completeModal}
          onClose={() => setCompleteModal(false)}
        />
      ) : null}
    </Actions>
  );
}

export default RevealSlideView;
